import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  startAfter,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  getGroupById,
  addMember,
  addGuestMember,
  removeMember,
  updateMemberStatus,
  transferOwnership,
  updateGroup,
} from '../services/groupService';
import { getUsersByIds } from '../services/userService';
import { approveMatch as approveMatchService } from '../services/matchService';
import { reactivateRating, deactivateRating } from '../services/ratingService';
import {
  COLLECTIONS,
  GROUPS_FIELDS,
  GROUP_MEMBERS_FIELDS,
  GROUP_MEMBER_STATUS,
  GROUP_MEMBER_ROLES,
  PERIODS_FIELDS,
  RATINGS_FIELDS,
  MATCHES_FIELDS,
  MATCH_STATUS,
  MATCH_TYPES,
  SET_FIELDS,
  SET_FIELD_VALUES,
  MATCH_TEAM_FIELDS,
  HALL_OF_FAME_FIELDS,
  USERS_FIELDS,
} from '../domain/dataModel';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import { MATCH_TYPE_LABELS, matchTypeBadgeClass } from '../utils/matchDisplay';

function IconArrowLeft({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPlus({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  );
}

function IconFilter({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconUserPlus({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8.5 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM20 8v6M23 11h-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconX({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
    </svg>
  );
}

function IconChevronDown({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconSearch({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
    </svg>
  );
}

function IconClock({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const MATCH_STATUS_LABELS = { pending_approval: 'In approvazione', approved: 'Approvata' };

/** Ritorna true se l'utente era in campo in almeno un set della partita. */
function wasUserOnCourt(match, userId) {
  const sets = match[MATCHES_FIELDS.SETS] || [];
  for (const s of sets) {
    const t1 = s[SET_FIELDS.TEAM1] || {};
    const t2 = s[SET_FIELDS.TEAM2] || {};
    const ids = [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2], t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]];
    if (ids.some((id) => id === userId)) return true;
  }
  return false;
}

/** Numero di approvazioni necessarie: min(3, numero di giocatori reali in partita). */
function getRequiredApprovals(match, members) {
  const sets = match[MATCHES_FIELDS.SETS] || [];
  const playerIds = new Set();
  for (const s of sets) {
    const t1 = s[SET_FIELDS.TEAM1] || {};
    const t2 = s[SET_FIELDS.TEAM2] || {};
    [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2], t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].forEach((id) => id && playerIds.add(id));
  }
  let realCount = 0;
  playerIds.forEach((playerId) => {
    const member = members.find((m) => m.id === playerId || m[GROUP_MEMBERS_FIELDS.USER_ID] === playerId);
    if (member && member[GROUP_MEMBERS_FIELDS.USER_ID] != null && !member[GROUP_MEMBERS_FIELDS.IS_GUEST]) realCount++;
  });
  return Math.min(3, realCount);
}

function formatDate(it) {
  if (!it?.toDate) return '—';
  return it.toDate().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
}

/** Estrae tutti i playerId da tutti i set della partita (team1.player1, team1.player2, team2.player1, team2.player2). */
function getAllPlayerIds(match) {
  const ids = new Set();
  const sets = match[MATCHES_FIELDS.SETS] || [];
  for (const s of sets) {
    const t1 = s[SET_FIELDS.TEAM1] || {};
    const t2 = s[SET_FIELDS.TEAM2] || {};
    [t1[MATCH_TEAM_FIELDS.PLAYER1], t1[MATCH_TEAM_FIELDS.PLAYER2], t2[MATCH_TEAM_FIELDS.PLAYER1], t2[MATCH_TEAM_FIELDS.PLAYER2]].forEach((id) => id && ids.add(id));
  }
  return [...ids];
}

function parseFiltersFromSearchParams(searchParams) {
  const dm = searchParams.get('dm');
  const dateMode = dm === 'range' ? 'range' : 'single';
  const ds = searchParams.get('ds');
  const dateSingle = ds && dayjs(ds, 'YYYY-MM-DD').isValid() ? dayjs(ds, 'YYYY-MM-DD') : null;
  const df = searchParams.get('df');
  const dateFrom = df && dayjs(df, 'YYYY-MM-DD').isValid() ? dayjs(df, 'YYYY-MM-DD') : null;
  const dt = searchParams.get('dt');
  const dateTo = dt && dayjs(dt, 'YYYY-MM-DD').isValid() ? dayjs(dt, 'YYYY-MM-DD') : null;
  const t = searchParams.get('t');
  const types = t ? t.split(',').filter((v) => ['classic', 'king', 'super_king'].includes(v)) : [];
  const om = searchParams.get('om');
  const onlyMine = om === '1';
  const p = searchParams.get('p');
  const playerIds = p ? p.split(',').filter(Boolean) : [];
  return {
    dateMode,
    dateSingle,
    dateFrom,
    dateTo,
    types,
    onlyMine,
    players: [],
    playerIds,
  };
}

function filtersToSearchParams(filters) {
  const params = {};
  if (filters.dateMode && filters.dateMode !== 'single') params.dm = filters.dateMode;
  if (filters.dateSingle && filters.dateSingle.isValid?.()) params.ds = filters.dateSingle.format('YYYY-MM-DD');
  if (filters.dateFrom && filters.dateFrom.isValid?.()) params.df = filters.dateFrom.format('YYYY-MM-DD');
  if (filters.dateTo && filters.dateTo.isValid?.()) params.dt = filters.dateTo.format('YYYY-MM-DD');
  if (filters.types?.length) params.t = filters.types.join(',');
  if (filters.onlyMine) params.om = '1';
  const playerIds = (filters.players || []).map((m) => m?.id).filter(Boolean);
  if (playerIds.length) params.p = playerIds.join(',');
  return params;
}

const defaultFiltersObj = {
  dateMode: 'single',
  dateSingle: null,
  dateFrom: null,
  dateTo: null,
  types: [],
  onlyMine: false,
  players: [],
  playerIds: [],
};

export default function GroupDetail() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [group, setGroup] = useState(null);
  const [myMembership, setMyMembership] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState(() => parseFiltersFromSearchParams(searchParams));
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [displayedMatches, setDisplayedMatches] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [filtersDraft, setFiltersDraft] = useState(defaultFiltersObj);
  const [matchesError, setMatchesError] = useState(null);
  const effectivePlayers = useMemo(() => {
    if (filters.players?.length) return filters.players;
    if (filters.playerIds?.length && members.length) return members.filter((m) => filters.playerIds.includes(m.id));
    return [];
  }, [filters.players, filters.playerIds, members]);
  const filtersWithPlayers = useMemo(() => ({ ...filters, players: effectivePlayers }), [filters, effectivePlayers]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [hallOfFame, setHallOfFame] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteSearchResults, setInviteSearchResults] = useState([]);
  const [inviteSearchLoading, setInviteSearchLoading] = useState(false);
  const [inviteSendingId, setInviteSendingId] = useState(null);
  const [acceptRejectLoadingId, setAcceptRejectLoadingId] = useState(null);
  const [pendingApprovalMatches, setPendingApprovalMatches] = useState([]);
  const [approveLoadingId, setApproveLoadingId] = useState(null);
  const [guestDialogOpen, setGuestDialogOpen] = useState(false);
  const [guestNickname, setGuestNickname] = useState('');
  const [guestRating, setGuestRating] = useState(1000);
  const [addGuestLoading, setAddGuestLoading] = useState(false);
  const [addGuestError, setAddGuestError] = useState('');
  const [removeMemberDialog, setRemoveMemberDialog] = useState(null);
  const [removeMemberLoading, setRemoveMemberLoading] = useState(false);
  const [leaveGroupDialogOpen, setLeaveGroupDialogOpen] = useState(false);
  const [leaveGroupStep, setLeaveGroupStep] = useState(1);
  const [leaveGroupLoading, setLeaveGroupLoading] = useState(false);
  const [becomeOwnerLoading, setBecomeOwnerLoading] = useState(false);
  /** periodId -> open; se assente, default solo per index === 0 */
  const [hofAccordionExplicit, setHofAccordionExplicit] = useState({});

  const isOwner = group && user?.uid && group[GROUPS_FIELDS.OWNER_ID] === user.uid;
  const activePeriod = useMemo(() => {
    const now = new Date();
    return periods.find((p) => {
      const startRaw = p[PERIODS_FIELDS.START_DATE];
      const endRaw = p[PERIODS_FIELDS.END_DATE];
      if (!startRaw || !endRaw) return false;
      const startDate = startRaw.toDate ? startRaw.toDate() : new Date(startRaw);
      const endDate = endRaw.toDate ? endRaw.toDate() : new Date(endRaw);
      return startDate <= now && now <= endDate;
    }) || null;
  }, [periods]);

  const closedPeriods = useMemo(() => {
    const now = new Date();
    return periods
      .filter((p) => {
        const endRaw = p[PERIODS_FIELDS.END_DATE];
        if (!endRaw) return false;
        const endDate = endRaw.toDate ? endRaw.toDate() : new Date(endRaw);
        return endDate < now;
      })
      .sort((a, b) => {
        const ea = a[PERIODS_FIELDS.END_DATE]?.toMillis?.() ?? 0;
        const eb = b[PERIODS_FIELDS.END_DATE]?.toMillis?.() ?? 0;
        return eb - ea;
      });
  }, [periods]);

  // Mappa periodId -> documento hallOfFame (con campo rankings)
  const hallOfFameByPeriod = useMemo(() => {
    const byPeriod = {};
    hallOfFame.forEach((doc) => {
      const pid = doc[HALL_OF_FAME_FIELDS.PERIOD_ID];
      if (pid) byPeriod[pid] = doc;
    });
    return byPeriod;
  }, [hallOfFame]);

  const formatPeriodDate = (ts) => {
    if (!ts?.toDate) return '—';
    return ts.toDate().toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Initial load: group + check access (must be member or owner)
  useEffect(() => {
    if (!groupId || !user?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const [groupData, membersSnap] = await Promise.all([
          getGroupById(groupId),
          getDocs(
            query(
              collection(db, COLLECTIONS.GROUP_MEMBERS),
              where(GROUP_MEMBERS_FIELDS.GROUP_ID, '==', groupId),
              where(GROUP_MEMBERS_FIELDS.USER_ID, '==', user.uid)
            )
          ),
        ]);
        if (cancelled) return;
        if (!groupData) {
          setError('Gruppo non trovato');
          setLoading(false);
          return;
        }
        const myMem = membersSnap.docs.find((d) => d.data()[GROUP_MEMBERS_FIELDS.STATUS] === GROUP_MEMBER_STATUS.ACTIVE)
          || membersSnap.docs[0];
        const isMember = myMem && (myMem.data()[GROUP_MEMBERS_FIELDS.STATUS] === GROUP_MEMBER_STATUS.ACTIVE);
        const isOwnerGroup = groupData[GROUPS_FIELDS.OWNER_ID] === user.uid;
        if (!isMember && !isOwnerGroup) {
          setError('Non hai accesso a questo gruppo');
          setLoading(false);
          return;
        }
        setGroup(groupData);
        setMyMembership(myMem ? { id: myMem.id, ...myMem.data() } : null);
      } catch (err) {
        setError(err.message || 'Errore di caricamento');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, user?.uid]);

  // Real-time: periods
  useEffect(() => {
    if (!groupId) return;
    const q = query(
      collection(db, COLLECTIONS.PERIODS),
      where(PERIODS_FIELDS.GROUP_ID, '==', groupId)
    );
    const unsub = onSnapshot(q, (snap) => {
      setPeriods(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, (err) => setError(err.message));
    return () => unsub();
  }, [groupId]);

  // Real-time: ratings for active period
  useEffect(() => {
    if (!groupId || !activePeriod?.id) {
      setRatings([]);
      return;
    }
    const q = query(
      collection(db, COLLECTIONS.RATINGS),
      where(RATINGS_FIELDS.GROUP_ID, '==', groupId),
      where(RATINGS_FIELDS.PERIOD_ID, '==', activePeriod.id),
      orderBy(RATINGS_FIELDS.RATING, 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setRatings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => setRatings([]));
    return () => unsub();
  }, [groupId, activePeriod?.id]);

  // Real-time: partite in attesa di approvazione (solo PENDING_APPROVAL)
  useEffect(() => {
    if (!groupId) return;
    const q = query(
      collection(db, COLLECTIONS.MATCHES),
      where(MATCHES_FIELDS.GROUP_ID, '==', groupId),
      where(MATCHES_FIELDS.STATUS, '==', MATCH_STATUS.PENDING_APPROVAL)
    );
    const unsub = onSnapshot(q, (snap) => {
      setPendingApprovalMatches(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => setPendingApprovalMatches([]));
    return () => unsub();
  }, [groupId]);

  // Real-time: active members
  useEffect(() => {
    if (!groupId) return;
    const q = query(
      collection(db, COLLECTIONS.GROUP_MEMBERS),
      where(GROUP_MEMBERS_FIELDS.GROUP_ID, '==', groupId),
      where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.ACTIVE)
    );
    const unsub = onSnapshot(q, (snap) => {
      setMembers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => setMembers([]));
    return () => unsub();
  }, [groupId]);

  // Real-time: pending requests (for owner)
  useEffect(() => {
    if (!groupId) return;
    const q = query(
      collection(db, COLLECTIONS.GROUP_MEMBERS),
      where(GROUP_MEMBERS_FIELDS.GROUP_ID, '==', groupId),
      where(GROUP_MEMBERS_FIELDS.STATUS, '==', GROUP_MEMBER_STATUS.PENDING_REQUEST)
    );
    const unsub = onSnapshot(q, (snap) => {
      setPendingRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => setPendingRequests([]));
    return () => unsub();
  }, [groupId]);

  // Hall of fame (all entries for this group)
  useEffect(() => {
    if (!groupId) return;
    const q = query(
      collection(db, COLLECTIONS.HALL_OF_FAME),
      where(HALL_OF_FAME_FIELDS.GROUP_ID, '==', groupId)
    );
    const unsub = onSnapshot(q, (snap) => {
      setHallOfFame(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => setHallOfFame([]));
    return () => unsub();
  }, [groupId]);

  // Load user display names for members, ratings, pending e approvatori partite (solo userId non null, mai passare null a doc())
  const userIds = useMemo(() => {
    const ids = new Set();
    members.forEach((m) => {
      const uid = m[GROUP_MEMBERS_FIELDS.USER_ID];
      if (uid != null && uid !== '') ids.add(uid);
    });
    pendingRequests.forEach((m) => {
      const uid = m[GROUP_MEMBERS_FIELDS.USER_ID];
      if (uid != null && uid !== '') ids.add(uid);
    });
    ratings.forEach((r) => {
      const uid = r[RATINGS_FIELDS.USER_ID];
      if (uid != null && uid !== '') ids.add(uid);
    });
    hallOfFame.forEach((h) => {
      (h[HALL_OF_FAME_FIELDS.RANKINGS] || []).forEach((r) => r.memberId && ids.add(r.memberId));
    });
    if (group) {
      const ownerId = group[GROUPS_FIELDS.OWNER_ID];
      if (ownerId != null && ownerId !== '') ids.add(ownerId);
    }
    pendingApprovalMatches.forEach((m) =>
      (m[MATCHES_FIELDS.APPROVALS] || []).forEach((a) => a.userId && ids.add(a.userId))
    );
    return [...ids].filter((id) => id != null && id !== '');
  }, [members, pendingRequests, ratings, hallOfFame, group, pendingApprovalMatches]);
  const userIdsKey = useMemo(() => userIds.join(','), [userIds]);

  useEffect(() => {
    const validIds = userIds.filter((uid) => uid != null && uid !== '');
    const validUserIds = validIds.filter((id) => {
      const m = members.find((x) => x.id === id);
      return !(m && m[GROUP_MEMBERS_FIELDS.IS_GUEST]);
    });
    if (validUserIds.length === 0) {
      setUsersMap({});
      return;
    }
    getUsersByIds(validUserIds).then((usersData) => {
      const map = {};
      validUserIds.forEach((uid) => {
        const u = usersData[uid];
        map[uid] = u
          ? { displayName: u[USERS_FIELDS.DISPLAY_NAME] || u[USERS_FIELDS.EMAIL] || uid, email: u[USERS_FIELDS.EMAIL] || null }
          : { displayName: uid, email: null };
      });
      setUsersMap(map);
    });
  }, [userIdsKey, userIds, members]);

  const applyClientFilters = (matchData, filterValues) => {
    return matchData.filter((match) => {
      if (filterValues.onlyMine) {
        const playerIds = getAllPlayerIds(match);
        const myId = myMembership?.id || user?.uid;
        if (!myId || (!playerIds.includes(myId) && !playerIds.includes(user?.uid))) return false;
      }
      if (filterValues.players?.length > 0) {
        const playerIds = getAllPlayerIds(match);
        const allPresent = filterValues.players.every((p) => {
          const pid = p[GROUP_MEMBERS_FIELDS.IS_GUEST] ? p.id : p[GROUP_MEMBERS_FIELDS.USER_ID];
          return pid && playerIds.includes(pid);
        });
        if (!allPresent) return false;
      }
      return true;
    });
  };

  const fetchMatches = async (filterValues, startAfterDoc = null) => {
    let q = query(
      collection(db, COLLECTIONS.MATCHES),
      where(MATCHES_FIELDS.GROUP_ID, '==', groupId),
      orderBy(MATCHES_FIELDS.PLAYED_AT, 'desc')
    );
    if (filterValues.types?.length > 0) {
      q = query(q, where(MATCHES_FIELDS.TYPE, 'in', filterValues.types));
    }
    if (filterValues.dateMode === 'single' && filterValues.dateSingle) {
      const d = filterValues.dateSingle;
      q = query(
        q,
        where(MATCHES_FIELDS.PLAYED_AT, '>=', Timestamp.fromDate(d.startOf('day').toDate())),
        where(MATCHES_FIELDS.PLAYED_AT, '<=', Timestamp.fromDate(d.endOf('day').toDate()))
      );
    } else if (filterValues.dateMode === 'range') {
      if (filterValues.dateFrom) {
        q = query(q, where(MATCHES_FIELDS.PLAYED_AT, '>=', Timestamp.fromDate(filterValues.dateFrom.startOf('day').toDate())));
      }
      if (filterValues.dateTo) {
        q = query(q, where(MATCHES_FIELDS.PLAYED_AT, '<=', Timestamp.fromDate(filterValues.dateTo.endOf('day').toDate())));
      }
    }
    if (startAfterDoc) q = query(q, startAfter(startAfterDoc));
    q = query(q, limit(10));
    const snapshot = await getDocs(q);
    return { docs: snapshot.docs, empty: snapshot.empty };
  };

  const loadMatches = async (filterValues, reset = false) => {
    setLoadingMore(true);
    setMatchesError(null);
    let accumulated = reset ? [] : [...displayedMatches];
    let currentLastDoc = reset ? null : lastDoc;
    let currentHasMore = reset ? true : hasMore;
    const initialLength = reset ? 0 : displayedMatches.length;
    const pageSize = 10;
    try {
      while (accumulated.length - initialLength < pageSize && currentHasMore) {
        const { docs, empty } = await fetchMatches(filterValues, currentLastDoc);
        if (empty || docs.length === 0) {
          currentHasMore = false;
          break;
        }
        const matchData = docs.map((d) => ({ id: d.id, ...d.data() }));
        const filtered = applyClientFilters(matchData, filterValues);
        accumulated = [...accumulated, ...filtered];
        currentLastDoc = docs[docs.length - 1];
        if (docs.length < pageSize) currentHasMore = false;
      }
      setDisplayedMatches(accumulated);
      setLastDoc(currentLastDoc);
      setHasMore(currentHasMore);
    } catch (err) {
      const msg = err?.message || '';
      if (msg.includes('index') || msg.includes('Index') || err?.code === 'failed-precondition') {
        const link = err?.message?.match(/https:\/\/[^\s]+/)?.[0];
        console.warn('Firestore: indice composto richiesto. Crea gli indici per groupId+playedAt e, se usi il filtro tipologia, per groupId+type+playedAt.', link || err);
      }
      setMatchesError(msg || 'Errore caricamento partite');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!groupId) return;
    loadMatches(filtersWithPlayers, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  useEffect(() => {
    if (!groupId || !members.length || !filters.playerIds?.length) return;
    const resolved = members.filter((m) => filters.playerIds.includes(m.id));
    setFilters((prev) => ({ ...prev, players: resolved, playerIds: [] }));
    loadMatches({ ...filters, players: resolved }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, members.length, filters.playerIds?.join(',')]);

  const openFiltersDrawer = () => {
    setFiltersDraft({ ...filters, playerIds: undefined });
    setFiltersDrawerOpen(true);
  };
  const applyFiltersAndClose = () => {
    setFilters(filtersDraft);
    setFiltersDrawerOpen(false);
    setSearchParams(filtersToSearchParams(filtersDraft), { replace: true });
    loadMatches(filtersDraft, true);
  };
  const resetFilters = () => {
    const next = { ...defaultFiltersObj };
    setFiltersDraft(next);
    setFilters(next);
    setFiltersDrawerOpen(false);
    setSearchParams({}, { replace: true });
    loadMatches(next, true);
  };
  const activeFiltersCount =
    ((filters.dateMode === 'single' && filters.dateSingle) || (filters.dateMode === 'range' && (filters.dateFrom || filters.dateTo)) ? 1 : 0) +
    (filters.types?.length || 0) +
    (filters.onlyMine ? 1 : 0) +
    (effectivePlayers.length || 0);

  const handleAcceptRequest = async (req) => {
    const membershipId = typeof req === 'string' ? req : req?.id;
    if (!membershipId) return;
    setAcceptRejectLoadingId(membershipId);
    try {
      await updateMemberStatus(membershipId, GROUP_MEMBER_STATUS.ACTIVE);
      const memberData = typeof req === 'object' && req ? req : { id: membershipId, [GROUP_MEMBERS_FIELDS.USER_ID]: null, [GROUP_MEMBERS_FIELDS.IS_GUEST]: false };
      const uid = memberData[GROUP_MEMBERS_FIELDS.USER_ID];
      if (uid != null && uid !== '' && activePeriod?.id && groupId) {
        await reactivateRating(memberData.id, uid, activePeriod.id, groupId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAcceptRejectLoadingId(null);
    }
  };

  const handleRejectRequest = async (membershipId) => {
    setAcceptRejectLoadingId(membershipId);
    try {
      await updateMemberStatus(membershipId, GROUP_MEMBER_STATUS.REJECTED);
    } catch (err) {
      setError(err.message);
    } finally {
      setAcceptRejectLoadingId(null);
    }
  };

  const handleRemoveMember = (member) => setRemoveMemberDialog(member);

  const confirmRemoveMember = async () => {
    const m = removeMemberDialog;
    if (!m || !groupId) return;
    setRemoveMemberLoading(true);
    setError(null);
    try {
      await removeMember(m.id);
      setRemoveMemberDialog(null);
    } catch (err) {
      setError(err.message || 'Errore durante la rimozione del membro.');
    } finally {
      setRemoveMemberLoading(false);
    }
  };

  const handleLeaveGroup = () => {
    setLeaveGroupStep(1);
    setLeaveGroupDialogOpen(true);
  };

  const confirmLeaveGroup = async () => {
    if (isOwner && leaveGroupStep === 1) {
      setLeaveGroupStep(2);
      return;
    }
    if (!myMembership?.id || !groupId) return;
    setLeaveGroupLoading(true);
    setError(null);
    try {
      await updateMemberStatus(myMembership.id, GROUP_MEMBER_STATUS.REJECTED);
      if (activePeriod?.id) {
        await deactivateRating(
          myMembership.id,
          myMembership[GROUP_MEMBERS_FIELDS.USER_ID] ?? null,
          activePeriod.id,
          groupId
        );
      }
      if (isOwner) {
        await updateGroup(groupId, { [GROUPS_FIELDS.OWNER_ID]: null });
      }
      setLeaveGroupDialogOpen(false);
      setLeaveGroupStep(1);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Errore durante l\'uscita dal gruppo.');
    } finally {
      setLeaveGroupLoading(false);
    }
  };

  const handleBecomeOwner = async () => {
    if (!group?.id || !user?.uid) return;
    setBecomeOwnerLoading(true);
    setError(null);
    try {
      await transferOwnership(group.id, user.uid);
    } catch (err) {
      setError(err.message || 'Errore durante l\'assegnazione del proprietario.');
    } finally {
      setBecomeOwnerLoading(false);
    }
  };

  const handleApproveMatch = async (match) => {
    setApproveLoadingId(match.id);
    setError(null);
    try {
      await approveMatchService(match.id, user.uid, members);
    } catch (err) {
      setError(err.message || 'Errore durante l\'approvazione');
    } finally {
      setApproveLoadingId(null);
    }
  };

  const handleInviteSearch = async () => {
    const term = inviteSearch.trim().toLowerCase();
    if (!term) {
      setInviteSearchResults([]);
      return;
    }
    setInviteSearchLoading(true);
    try {
      const [emailSnap, nameSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, COLLECTIONS.USERS),
            where(USERS_FIELDS.EMAIL, '>=', term),
            where(USERS_FIELDS.EMAIL, '<=', term + '\uf8ff'),
            limit(15)
          )
        ),
        getDocs(
          query(
            collection(db, COLLECTIONS.USERS),
            where(USERS_FIELDS.DISPLAY_NAME, '>=', term),
            where(USERS_FIELDS.DISPLAY_NAME, '<=', term + '\uf8ff'),
            limit(15)
          )
        ),
      ]);
      const seen = new Set();
      const list = [];
      [...emailSnap.docs, ...nameSnap.docs].forEach((d) => {
        if (seen.has(d.id)) return;
        seen.add(d.id);
        list.push({ id: d.id, ...d.data() });
      });
      setInviteSearchResults(list);
    } catch (err) {
      setInviteSearchResults([]);
    } finally {
      setInviteSearchLoading(false);
    }
  };

  const handleInviteUser = async (userId) => {
    setInviteSendingId(userId);
    try {
      await addMember(groupId, userId, GROUP_MEMBER_ROLES.MEMBER);
      setInviteSearchResults((prev) => prev.filter((u) => u.id !== userId));
    } catch (err) {
      setError(err.message);
    } finally {
      setInviteSendingId(null);
    }
  };

  const handleAddGuest = async () => {
    const nickname = guestNickname.trim();
    if (!nickname) {
      setAddGuestError('Inserisci il nickname dell\'ospite.');
      return;
    }
    if (!groupId) return;
    setAddGuestLoading(true);
    setAddGuestError('');
    try {
      const initialRating = Math.min(3000, Math.max(0, Number(guestRating) || 1000));
      await addGuestMember(groupId, nickname, initialRating, activePeriod?.id ?? undefined);
      setGuestDialogOpen(false);
      setGuestNickname('');
      setGuestRating(1000);
    } catch (err) {
      setAddGuestError(err.message || 'Errore nella creazione dell\'ospite.');
    } finally {
      setAddGuestLoading(false);
    }
  };

  const memberIds = useMemo(() => new Set(members.map((m) => m[GROUP_MEMBERS_FIELDS.USER_ID])), [members]);
  const displayName = (uid) => usersMap[uid]?.displayName || uid;
  const ratingsByGuestMemberId = useMemo(() => {
    const map = {};
    ratings.forEach((r) => {
      const mid = r[RATINGS_FIELDS.MEMBER_ID];
      if (mid) map[mid] = r;
    });
    return map;
  }, [ratings]);

  /** Solo rating attivi in classifica (retrocompatibilità: isActive undefined = attivo). */
  const activeRatings = useMemo(
    () => ratings
      .filter((r) => r[RATINGS_FIELDS.IS_ACTIVE] !== false)
      .sort((a, b) => (b[RATINGS_FIELDS.RATING] ?? 0) - (a[RATINGS_FIELDS.RATING] ?? 0)),
    [ratings]
  );
  const getMemberDisplayName = (m) =>
    m[GROUP_MEMBERS_FIELDS.IS_GUEST] ? (m[GROUP_MEMBERS_FIELDS.GUEST_NICKNAME] || 'Ospite') : displayName(m[GROUP_MEMBERS_FIELDS.USER_ID]);
  const getRankingMemberDisplayName = (memberId) => {
    const m = members.find((x) => x.id === memberId);
    if (m && m[GROUP_MEMBERS_FIELDS.IS_GUEST]) return m[GROUP_MEMBERS_FIELDS.GUEST_NICKNAME] || 'Ospite';
    return displayName(memberId);
  };

  /** Per la tabella Classifica: risolve nome, isGuest e userId da una riga rating (guest da memberId + guestNickname). */
  const getClassificaPlayerInfo = (r) => {
    const memberId = r[RATINGS_FIELDS.MEMBER_ID];
    const userIdFromRating = r[RATINGS_FIELDS.USER_ID];
    if (memberId) {
      const m = members.find((x) => x.id === memberId);
      if (m) {
        if (m[GROUP_MEMBERS_FIELDS.IS_GUEST])
          return { displayName: m[GROUP_MEMBERS_FIELDS.GUEST_NICKNAME] || 'Ospite', isGuest: true, userId: null };
        return {
          displayName: usersMap[m[GROUP_MEMBERS_FIELDS.USER_ID]]?.displayName ?? 'Giocatore sconosciuto',
          isGuest: false,
          userId: m[GROUP_MEMBERS_FIELDS.USER_ID],
        };
      }
      return { displayName: 'Giocatore sconosciuto', isGuest: false, userId: null };
    }
    if (userIdFromRating) {
      return {
        displayName: usersMap[userIdFromRating]?.displayName ?? 'Giocatore sconosciuto',
        isGuest: false,
        userId: userIdFromRating,
      };
    }
    return { displayName: 'Giocatore sconosciuto', isGuest: false, userId: null };
  };

  /** Colore sfondo avatar da hash dello userId (utenti registrati). */
  const avatarColorFromUserId = (userId) => {
    if (!userId) return undefined;
    let h = 0;
    for (let i = 0; i < userId.length; i++) h = (h << 5) - h + userId.charCodeAt(i);
    const hue = Math.abs(h % 360);
    return `hsl(${hue}, 55%, 45%)`;
  };

  /** Iniziali da displayName (max 2 caratteri). */
  const getInitials = (name) => {
    if (!name || name === 'Giocatore sconosciuto') return '?';
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    return name.slice(0, 2).toUpperCase();
  };

  const isHofAccordionOpen = (periodId, index) =>
    Object.prototype.hasOwnProperty.call(hofAccordionExplicit, periodId)
      ? hofAccordionExplicit[periodId]
      : index === 0;
  const toggleHofAccordion = (periodId, index) => {
    setHofAccordionExplicit((prev) => {
      const cur = Object.prototype.hasOwnProperty.call(prev, periodId)
        ? prev[periodId]
        : index === 0;
      return { ...prev, [periodId]: !cur };
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-royal border-t-transparent" />
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm text-brand-ice/70 hover:text-brand-ice"
        >
          <IconArrowLeft className="h-4 w-4 shrink-0" />
          Indietro
        </button>
        <div className="rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error || 'Gruppo non trovato'}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-6">
      {error && (
        <div className="mb-2 rounded-btn border border-loss/60 bg-loss/15 px-4 py-3 text-sm text-red-100">
          {error}
        </div>
      )}

      {/* Banner: gruppo senza proprietario (solo membri attivi registrati) */}
      {group[GROUPS_FIELDS.OWNER_ID] == null &&
        myMembership != null &&
        myMembership[GROUP_MEMBERS_FIELDS.STATUS] === GROUP_MEMBER_STATUS.ACTIVE &&
        myMembership[GROUP_MEMBERS_FIELDS.IS_GUEST] === false && (
        <div className="mb-4 rounded-card border border-gold/30 bg-gold/10 p-4 text-sm text-brand-ice">
          <p className="mb-2">
            Questo gruppo non ha un proprietario. Vuoi diventare il proprietario?
          </p>
          <Button
            type="button"
            variant="warning"
            size="sm"
            onClick={handleBecomeOwner}
            disabled={becomeOwnerLoading}
          >
            {becomeOwnerLoading ? 'In corso…' : 'Diventa proprietario'}
          </Button>
        </div>
      )}

      {/* 1. INTESTAZIONE */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => navigate('/')}
          aria-label="Torna alla Dashboard"
        >
          <IconArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-display text-2xl font-bold text-brand-ice">
          {group[GROUPS_FIELDS.NAME] || 'Gruppo'}
        </h1>
        {isOwner && <Badge>Owner</Badge>}
        {myMembership && (
          <Button type="button" variant="outline" size="sm" className="ml-auto" onClick={handleLeaveGroup}>
            Abbandona gruppo
          </Button>
        )}
      </div>

      {/* 2. PERIODO ATTIVO */}
      <h2 className="mb-2 mt-6 font-display text-lg font-semibold text-brand-ice">
        Periodo attivo
      </h2>
      {activePeriod ? (
        <Card className="mb-2 border-surface-muted bg-surface-card">
          <CardContent className="space-y-2 p-4">
            <p className="font-semibold text-brand-ice">{activePeriod[PERIODS_FIELDS.NAME]}</p>
            <p className="text-sm text-brand-ice/60">
              Da {formatDate(activePeriod[PERIODS_FIELDS.START_DATE])} a {formatDate(activePeriod[PERIODS_FIELDS.END_DATE])}
            </p>
            {isOwner && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/groups/${groupId}/periods`)}
              >
                Gestisci periodi
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-2 border-surface-muted bg-surface-card">
          <CardContent className="space-y-2 p-4">
            <p className="text-sm text-brand-ice/60">Nessun periodo attivo.</p>
            {isOwner && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/groups/${groupId}/periods`)}
              >
                Gestisci periodi
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* 3. CLASSIFICA */}
      <h2 className="mb-2 mt-6 font-display text-lg font-semibold text-brand-ice">
        Classifica
      </h2>
      {!activePeriod ? (
        <Card className="mb-2 border-surface-muted bg-surface-card">
          <CardContent className="p-4">
            <p className="text-sm text-brand-ice/60">Nessuna classifica disponibile.</p>
          </CardContent>
        </Card>
      ) : activeRatings.length === 0 ? (
        <Card className="mb-2 border-surface-muted bg-surface-card">
          <CardContent className="p-4">
            <p className="text-sm text-brand-ice/60">Nessun dato in classifica per questo periodo.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="mb-2 space-y-2">
          {activeRatings.map((r, idx) => {
            const player = getClassificaPlayerInfo(r);
            const played = r[RATINGS_FIELDS.MATCHES_PLAYED] ?? 0;
            const wins = r[RATINGS_FIELDS.WINS] ?? 0;
            const winRate = played > 0 ? Math.round((wins / played) * 100) : 0;
            return (
              <div
                key={r.id}
                className="flex items-center gap-3 rounded-card border border-surface-muted bg-surface-card px-4 py-3"
              >
                <span
                  className={`w-7 text-center font-display text-lg font-bold ${
                    idx === 0 ? 'text-gold' : idx === 1 ? 'text-brand-ice/70' : idx === 2 ? 'text-amber-600' : 'text-brand-ice/40'
                  }`}
                >
                  {idx + 1}
                </span>
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                  style={{ backgroundColor: player.isGuest ? '#10B981' : avatarColorFromUserId(player.userId) }}
                >
                  {player.isGuest ? '👤' : getInitials(player.displayName)}
                </div>
                <span className="flex-1 font-medium text-brand-ice">{player.displayName}</span>
                {player.isGuest && <span className="text-xs text-win">Ospite</span>}
                <div className="text-right">
                  <p className="font-display text-lg font-bold text-brand-ice">{r[RATINGS_FIELDS.RATING] ?? 0}</p>
                  <p className="text-xs text-brand-ice/50">
                    {played}G · {winRate}%
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Partite in attesa di approvazione */}
      {pendingApprovalMatches.length > 0 && (
        <Card className="mb-4 border border-brand-royal/30 bg-brand-royal/10 shadow-none">
          <CardContent className="p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-semibold text-brand-ice">Partite in attesa</h2>
              <Badge variant="default" className="shrink-0">
                {pendingApprovalMatches.length}
              </Badge>
            </div>
            <div className="space-y-2">
              {pendingApprovalMatches.map((m) => {
                const typeKey = m[MATCHES_FIELDS.TYPE] || 'classic';
                const date = m[MATCHES_FIELDS.PLAYED_AT] || m[MATCHES_FIELDS.CREATED_AT];
                const approvals = m[MATCHES_FIELDS.APPROVALS] || [];
                const count = approvals.length;
                const requiredApprovals = getRequiredApprovals(m, members);
                const createdBy = m[MATCHES_FIELDS.CREATED_BY];
                const hasAlreadyApproved = user?.uid && approvals.some((a) => a.userId === user.uid);
                const wasInMatch = user?.uid ? wasUserOnCourt(m, user.uid) : false;
                const isGroupOwner = !!isOwner;
                const canApprove = user?.uid && (wasInMatch || isGroupOwner) && !hasAlreadyApproved;
                const sets = m[MATCHES_FIELDS.SETS] || [];
                const approveLoading = approveLoadingId === m.id;
                const goMatch = () => navigate(`/groups/${groupId}/matches/${m.id}`, {
                  state: { fromSearch: location.search },
                });
                return (
                  <div
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={goMatch}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        goMatch();
                      }
                    }}
                    className="flex cursor-pointer flex-col gap-3 rounded-lg border border-brand-royal/20 bg-surface-card/80 p-3 sm:flex-row sm:items-center"
                  >
                    <div className="min-w-0 flex-1 space-y-2">
                      <p className="font-medium text-brand-ice">
                        {MATCH_TYPE_LABELS[typeKey] || typeKey} · {formatDate(date)}
                      </p>
                      <p className="text-sm text-brand-ice/60">
                        Inserita da {displayName(createdBy)} · {count}/{requiredApprovals} approvazioni
                      </p>
                      {sets.length > 0 && (
                        <p className="text-xs text-brand-ice/50">
                          Set:{' '}
                          {sets.map((s, i) => `${s[SET_FIELDS.SCORE1] ?? 0}-${s[SET_FIELDS.SCORE2] ?? 0}`).join(', ')}
                        </p>
                      )}
                      {approvals.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          <span className="w-full text-xs text-brand-ice/50">Approvato da:</span>
                          {approvals.map((a) => (
                            <Badge key={a.userId} variant="win" className="text-[10px]">
                              {displayName(a.userId)}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    {canApprove && (
                      <Button
                        type="button"
                        size="sm"
                        className="shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleApproveMatch(m);
                        }}
                        disabled={approveLoading}
                      >
                        {approveLoading ? '…' : 'Approva'}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 4. PARTITE RECENTI (filtri ibridi + paginazione server-side) */}
      <h2 className="mb-2 mt-6 font-display text-lg font-semibold text-brand-ice">
        Partite recenti
      </h2>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          className="gap-2"
          onClick={() => navigate(`/groups/${groupId}/add-match`)}
        >
          <IconPlus className="h-4 w-4" />
          Aggiungi partita
        </Button>
        <div className="relative">
          <Button type="button" variant="outline" size="sm" className="gap-2" onClick={openFiltersDrawer}>
            <IconFilter className="h-4 w-4" />
            Filtri
          </Button>
          {activeFiltersCount > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-surface-card"
              aria-hidden
            />
          )}
        </div>
      </div>
      {matchesError && (
        <div className="mb-2 flex items-start justify-between gap-2 rounded-btn border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
          <span>{matchesError}</span>
          <button
            type="button"
            className="shrink-0 text-amber-200/90 hover:text-amber-50"
            onClick={() => setMatchesError(null)}
          >
            Chiudi
          </button>
        </div>
      )}
      {displayedMatches.length === 0 && !loadingMore ? (
        <Card className="mb-2 border-surface-muted bg-surface-card">
          <CardContent className="p-4">
            <p className="text-sm text-brand-ice/60">Nessuna partita recente.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mb-2 space-y-2">
            {displayedMatches.map((m) => {
              const typeKey = m.matchType || m.type || m[MATCHES_FIELDS.TYPE] || 'classic';
              const statusKey = m.matchStatus || m.status || m[MATCHES_FIELDS.STATUS] || 'pending';
              const date = m[MATCHES_FIELDS.PLAYED_AT] || m[MATCHES_FIELDS.CREATED_AT];
              const sets = m[MATCHES_FIELDS.SETS] || [];
              const setsToCount = typeKey === MATCH_TYPES.SUPER_KING
                ? sets.filter((s) => s[SET_FIELDS.FIELD] === SET_FIELD_VALUES.GOLD_FINAL)
                : sets;
              let setsWon1 = 0;
              let setsWon2 = 0;
              setsToCount.forEach((s) => {
                const score1 = Number(s[SET_FIELDS.SCORE1]) || 0;
                const score2 = Number(s[SET_FIELDS.SCORE2]) || 0;
                if (score1 > score2) setsWon1++;
                else if (score2 > score1) setsWon2++;
              });
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => navigate(`/groups/${groupId}/matches/${m.id}`, {
                    state: { fromSearch: location.search },
                  })}
                  className="w-full rounded-card border border-surface-muted bg-surface-card p-4 text-left transition-colors hover:border-brand-royal/40"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span
                      className={cn(
                        'rounded-badge border px-2 py-0.5 font-ui text-xs font-semibold',
                        matchTypeBadgeClass(typeKey)
                      )}
                    >
                      {MATCH_TYPE_LABELS[typeKey] || typeKey}
                    </span>
                  </div>
                  <p className="my-3 text-center font-display text-3xl font-bold tabular-nums text-brand-ice">
                    {setsWon1} - {setsWon2}
                  </p>
                  <div className="flex items-center justify-between border-t border-surface-muted pt-2 text-xs text-brand-ice/50">
                    <span>{formatDate(date)}</span>
                    <span>{MATCH_STATUS_LABELS[statusKey] || statusKey}</span>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-brand-ice/50">
              Visualizzate {displayedMatches.length} partite
            </p>
            {hasMore && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={loadingMore}
                onClick={() => loadMatches(filtersWithPlayers, false)}
                className="gap-2"
              >
                {loadingMore && (
                  <span className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-brand-royal border-t-transparent" />
                )}
                {loadingMore ? 'Caricamento…' : 'Carica altre'}
              </Button>
            )}
          </div>
        </>
      )}

      {/* 5. MEMBRI */}
      <h2 className="mb-2 mt-6 font-display text-lg font-semibold text-brand-ice">
        Membri
      </h2>
      {isOwner && (
        <div className="mb-2 flex flex-wrap gap-2">
          <Button type="button" size="sm" className="gap-2" onClick={() => setInviteDialogOpen(true)}>
            <IconUserPlus className="h-4 w-4" />
            Invita membro
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setGuestDialogOpen(true);
              setAddGuestError('');
              setGuestNickname('');
              setGuestRating(1000);
            }}
          >
            Aggiungi ospite
          </Button>
        </div>
      )}
      <Card className="mb-2 border-surface-muted bg-surface-card">
        <CardContent className="divide-y divide-surface-muted p-0">
          {members.map((mem) => {
            const uid = mem[GROUP_MEMBERS_FIELDS.USER_ID];
            const isOwnerMember = !mem[GROUP_MEMBERS_FIELDS.IS_GUEST] && group[GROUPS_FIELDS.OWNER_ID] === uid;
            const isGuest = mem[GROUP_MEMBERS_FIELDS.IS_GUEST];
            const role = mem[GROUP_MEMBERS_FIELDS.ROLE];
            const guestRatingDoc = isGuest ? ratingsByGuestMemberId[mem.id] : null;
            const guestRatingVal = guestRatingDoc?.[RATINGS_FIELDS.RATING];
            const name = getMemberDisplayName(mem);
            return (
              <div key={mem.id} className="flex items-center gap-3 px-4 py-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                  style={{
                    backgroundColor: isGuest ? '#10B981' : avatarColorFromUserId(uid),
                  }}
                >
                  {isGuest ? '👤' : getInitials(name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-brand-ice">{name}</p>
                  {isGuest && guestRatingVal != null && (
                    <p className="text-xs text-brand-ice/50">Rating: {guestRatingVal}</p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                  {isOwnerMember && <Badge variant="gold">Owner</Badge>}
                  {isGuest && <Badge variant="pending">Ospite</Badge>}
                  {!isOwnerMember && !isGuest && role !== GROUP_MEMBER_ROLES.ADMIN && (
                    <Badge variant="outline">Membro</Badge>
                  )}
                  {isOwner && !isOwnerMember && (
                    <button
                      type="button"
                      className="ml-1 rounded-md p-2 text-loss hover:bg-loss/15"
                      aria-label="Rimuovi membro"
                      onClick={() => handleRemoveMember(mem)}
                    >
                      <IconTrash className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Pannello filtri partite recenti */}
      {filtersDrawerOpen && (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-labelledby="filters-drawer-title">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Chiudi filtri"
            onClick={() => setFiltersDrawerOpen(false)}
          />
          <div className="absolute right-0 top-0 flex h-full w-80 flex-col border-l border-surface-muted bg-surface-card shadow-xl">
            <div className="flex shrink-0 items-center justify-between border-b border-surface-muted px-4 py-3">
              <h2 id="filters-drawer-title" className="font-display text-lg font-semibold text-brand-ice">
                Filtra partite
              </h2>
              <button
                type="button"
                className="rounded-md p-2 text-brand-ice/70 hover:bg-surface-muted hover:text-brand-ice"
                aria-label="Chiudi"
                onClick={() => setFiltersDrawerOpen(false)}
              >
                <IconX className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                <div>
                  <Label className="text-brand-ice/80">Data</Label>
                  <div className="mt-2 flex gap-2">
                    <button
                      type="button"
                      className={cn(
                        'flex-1 rounded-full border px-3 py-2 text-xs font-medium transition-colors',
                        filtersDraft.dateMode === 'single'
                          ? 'border-brand-royal bg-brand-royal/20 text-brand-ice'
                          : 'border-surface-muted text-brand-ice/70 hover:border-brand-royal/40'
                      )}
                      onClick={() => setFiltersDraft((prev) => ({ ...prev, dateMode: 'single' }))}
                    >
                      Giorno singolo
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'flex-1 rounded-full border px-3 py-2 text-xs font-medium transition-colors',
                        filtersDraft.dateMode === 'range'
                          ? 'border-brand-royal bg-brand-royal/20 text-brand-ice'
                          : 'border-surface-muted text-brand-ice/70 hover:border-brand-royal/40'
                      )}
                      onClick={() => setFiltersDraft((prev) => ({ ...prev, dateMode: 'range' }))}
                    >
                      Intervallo
                    </button>
                  </div>
                  {filtersDraft.dateMode === 'single' && (
                    <div className="mt-3">
                      <DatePicker
                        label="Giorno"
                        value={filtersDraft.dateSingle}
                        onChange={(d) => setFiltersDraft((prev) => ({ ...prev, dateSingle: d || null }))}
                        maxDate={dayjs()}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </div>
                  )}
                  {filtersDraft.dateMode === 'range' && (
                    <div className="mt-3 flex flex-col gap-2">
                      <DatePicker
                        label="Da"
                        value={filtersDraft.dateFrom}
                        onChange={(d) => setFiltersDraft((prev) => ({ ...prev, dateFrom: d || null }))}
                        maxDate={dayjs()}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                      <DatePicker
                        label="A"
                        value={filtersDraft.dateTo}
                        onChange={(d) => setFiltersDraft((prev) => ({ ...prev, dateTo: d || null }))}
                        maxDate={dayjs()}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </div>
                  )}
                </div>
              </LocalizationProvider>

              <div>
                <Label className="text-brand-ice/80">Partecipazione</Label>
                <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-brand-ice">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-surface-muted bg-surface-card text-brand-royal focus:ring-brand-sky"
                    checked={!!filtersDraft.onlyMine}
                    onChange={(e) => setFiltersDraft((prev) => ({ ...prev, onlyMine: e.target.checked }))}
                  />
                  Solo le mie partite
                </label>
              </div>

              <div>
                <Label className="text-brand-ice/80">Tipologia</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { v: MATCH_TYPES.CLASSIC, label: 'Classica' },
                    { v: MATCH_TYPES.KING, label: 'King' },
                    { v: MATCH_TYPES.SUPER_KING, label: 'Super King' },
                  ].map(({ v, label }) => {
                    const selected = (filtersDraft.types || []).includes(v);
                    return (
                      <button
                        key={v}
                        type="button"
                        className={cn(
                          'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                          selected
                            ? 'border-brand-royal bg-brand-royal/20 text-brand-ice'
                            : 'border-surface-muted text-brand-ice/70 hover:border-brand-royal/40'
                        )}
                        onClick={() => {
                          setFiltersDraft((prev) => {
                            const types = [...(prev.types || [])];
                            const i = types.indexOf(v);
                            if (i >= 0) types.splice(i, 1);
                            else types.push(v);
                            return { ...prev, types };
                          });
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-brand-ice/80">Giocatori</Label>
                <div className="mt-2 max-h-48 space-y-2 overflow-y-auto rounded-md border border-surface-muted p-2">
                  {members.map((mem) => {
                    const checked = (filtersDraft.players || []).some((p) => p.id === mem.id);
                    return (
                      <label
                        key={mem.id}
                        className="flex cursor-pointer items-center gap-2 text-sm text-brand-ice"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-surface-muted bg-surface-card text-brand-royal focus:ring-brand-sky"
                          checked={checked}
                          onChange={() => {
                            setFiltersDraft((prev) => {
                              const cur = [...(prev.players || [])];
                              const has = cur.some((p) => p.id === mem.id);
                              return {
                                ...prev,
                                players: has ? cur.filter((p) => p.id !== mem.id) : [...cur, mem],
                              };
                            });
                          }}
                        />
                        <span className="truncate">{getMemberDisplayName(mem)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-2">
                <Button type="button" variant="success" className="w-full" onClick={applyFiltersAndClose}>
                  Applica filtri
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-loss hover:bg-loss/10 hover:text-loss"
                  onClick={resetFilters}
                >
                  Azzera filtri
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dialog rimozione membro (owner) */}
      {removeMemberDialog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="remove-member-title">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Chiudi"
            onClick={() => !removeMemberLoading && setRemoveMemberDialog(null)}
          />
          <Card className="relative z-10 w-full max-w-md border-surface-muted bg-surface-card shadow-xl">
            <CardContent className="space-y-4 p-6">
              <h2 id="remove-member-title" className="font-display text-lg font-semibold text-brand-ice">
                Rimuovi membro
              </h2>
              <p className="text-sm text-brand-ice/80">
                Sei sicuro di voler rimuovere {getMemberDisplayName(removeMemberDialog)} dal gruppo?
              </p>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRemoveMemberDialog(null)}
                  disabled={removeMemberLoading}
                >
                  Annulla
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={confirmRemoveMember}
                  disabled={removeMemberLoading}
                >
                  {removeMemberLoading ? 'In corso…' : 'Rimuovi'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog abbandono gruppo */}
      {leaveGroupDialogOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="leave-group-title">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Chiudi"
            onClick={() => !leaveGroupLoading && (setLeaveGroupDialogOpen(false), setLeaveGroupStep(1))}
          />
          <Card className="relative z-10 w-full max-w-md border-surface-muted bg-surface-card shadow-xl">
            <CardContent className="space-y-4 p-6">
              <h2 id="leave-group-title" className="font-display text-lg font-semibold text-brand-ice">
                Abbandona gruppo
              </h2>
              <p className="text-sm text-brand-ice/80">
                {isOwner && leaveGroupStep === 1
                  ? 'Sei sicuro di voler abbandonare il gruppo? Essendo il proprietario, il gruppo rimarrà senza owner finché un altro membro non si auto-eleggerà.'
                  : isOwner && leaveGroupStep === 2
                    ? 'Conferma definitiva: stai per abbandonare il gruppo. Questa azione non può essere annullata.'
                    : 'Sei sicuro di voler abbandonare il gruppo? Potrai essere reinvitato in futuro.'}
              </p>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setLeaveGroupDialogOpen(false); setLeaveGroupStep(1); }}
                  disabled={leaveGroupLoading}
                >
                  Annulla
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={confirmLeaveGroup}
                  disabled={leaveGroupLoading}
                >
                  {leaveGroupLoading ? 'In corso…' : leaveGroupStep === 2 ? 'Conferma abbandono' : 'Abbandona'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {isOwner && pendingRequests.length > 0 && (
        <Card className="mb-4 mt-6 border-surface-muted bg-surface-card">
          <CardContent className="space-y-3 p-4">
            <h2 className="font-display text-lg font-semibold text-brand-ice">Richieste in entrata</h2>
            <div className="space-y-3">
              {pendingRequests.map((req) => {
                const uid = req[GROUP_MEMBERS_FIELDS.USER_ID];
                const userInfo = usersMap[uid];
                const name = userInfo?.displayName || uid;
                const email = userInfo?.email || null;
                const requestDate = req[GROUP_MEMBERS_FIELDS.UPDATED_AT] || req[GROUP_MEMBERS_FIELDS.JOINED_AT];
                const requestDateStr = requestDate?.toDate
                  ? requestDate.toDate().toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
                  : '—';
                const loadingReq = acceptRejectLoadingId === req.id;
                return (
                  <div
                    key={req.id}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-surface-muted bg-surface-card/80 p-3"
                  >
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                      style={{ backgroundColor: avatarColorFromUserId(uid) }}
                    >
                      {getInitials(name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-brand-ice">{name}</p>
                      {email && <p className="truncate text-sm text-brand-ice/60">{email}</p>}
                      <div className="mt-1 flex items-center gap-1 text-xs text-brand-ice/50">
                        <IconClock className="h-3.5 w-3.5 shrink-0" />
                        {requestDateStr}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="success"
                        onClick={() => handleAcceptRequest(req)}
                        disabled={loadingReq}
                      >
                        Accetta
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRejectRequest(req.id)}
                        disabled={loadingReq}
                      >
                        Rifiuta
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 6. ALBO D'ORO */}
      <h2 className="mb-2 mt-6 font-display text-lg font-semibold text-brand-ice">
        Albo d&apos;oro
      </h2>
      {closedPeriods.length === 0 ? (
        <Card className="mb-4 border-surface-muted bg-surface-card">
          <CardContent className="p-8 text-center">
            <p className="mb-2 text-4xl">🏆</p>
            <p className="text-sm text-brand-ice/60">
              Nessun periodo concluso. Il primo albo d&apos;oro verrà creato alla chiusura del periodo attivo.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="mb-4 space-y-2">
          {closedPeriods.map((period, index) => {
            const hofDoc = hallOfFameByPeriod[period.id];
            const rankings = (hofDoc && hofDoc[HALL_OF_FAME_FIELDS.RANKINGS]) || [];
            const podium = rankings.slice(0, 3);
            const rest = rankings.slice(3);
            const winnerName = rankings[0] ? getRankingMemberDisplayName(rankings[0].memberId) : null;
            const periodLabel = `${period[PERIODS_FIELDS.NAME]} (${formatPeriodDate(period[PERIODS_FIELDS.START_DATE])} – ${formatPeriodDate(period[PERIODS_FIELDS.END_DATE])})`;
            const open = isHofAccordionOpen(period.id, index);

            return (
              <div
                key={period.id}
                className="overflow-hidden rounded-card border border-surface-muted bg-surface-card"
              >
                <button
                  type="button"
                  onClick={() => toggleHofAccordion(period.id, index)}
                  className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-muted/30"
                  aria-expanded={open}
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-semibold text-brand-ice">{periodLabel}</span>
                    {winnerName && (
                      <span className="mt-1 block text-sm text-brand-ice/70">
                        🥇 {winnerName}
                      </span>
                    )}
                  </div>
                  <IconChevronDown
                    className={cn('mt-0.5 h-5 w-5 shrink-0 text-brand-ice/60 transition-transform', open && 'rotate-180')}
                  />
                </button>
                {open && (
                  <div className="border-t border-surface-muted px-4 py-4">
                    <div className="mb-6 flex min-h-[11rem] items-end justify-center gap-2 sm:gap-3">
                      {podium[1] && (
                        <div className="w-[28%] max-w-[7.5rem] shrink-0 text-center">
                          <div className="rounded-card border border-surface-muted bg-surface-muted/40 p-3 text-brand-ice">
                            <p className="text-2xl">🥈</p>
                            <p className="mt-1 text-sm font-bold leading-tight">
                              {getRankingMemberDisplayName(podium[1].memberId)}
                            </p>
                            <p className="mt-1 text-xs text-brand-ice/70">
                              Rating: {podium[1].finalRating ?? '—'}
                            </p>
                            <p className="text-xs text-brand-ice/60">
                              Win rate: {typeof podium[1].winRate === 'number' ? `${podium[1].winRate}%` : '—'}
                            </p>
                          </div>
                        </div>
                      )}
                      {podium[0] && (
                        <div className="w-[32%] max-w-[8.5rem] shrink-0 text-center">
                          <div className="rounded-card border border-gold/40 bg-gold/20 p-3 text-brand-ice">
                            <p className="text-3xl">🥇</p>
                            <p className="mt-1 text-sm font-bold leading-tight">
                              {getRankingMemberDisplayName(podium[0].memberId)}
                            </p>
                            <p className="mt-1 text-xs text-brand-ice/80">
                              Rating: {podium[0].finalRating ?? '—'}
                            </p>
                            <p className="text-xs text-brand-ice/70">
                              Win rate: {typeof podium[0].winRate === 'number' ? `${podium[0].winRate}%` : '—'}
                            </p>
                          </div>
                        </div>
                      )}
                      {podium[2] && (
                        <div className="w-[28%] max-w-[7.5rem] shrink-0 text-center">
                          <div className="rounded-card border border-amber-700/40 bg-amber-800/20 p-3 text-brand-ice">
                            <p className="text-2xl">🥉</p>
                            <p className="mt-1 text-sm font-bold leading-tight">
                              {getRankingMemberDisplayName(podium[2].memberId)}
                            </p>
                            <p className="mt-1 text-xs text-brand-ice/80">
                              Rating: {podium[2].finalRating ?? '—'}
                            </p>
                            <p className="text-xs text-brand-ice/70">
                              Win rate: {typeof podium[2].winRate === 'number' ? `${podium[2].winRate}%` : '—'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    {rest.length > 0 && (
                      <div className="divide-y divide-surface-muted rounded-lg border border-surface-muted">
                        <div className="grid grid-cols-[2.5rem_minmax(0,1fr)_3.5rem_3.5rem_3.5rem_3rem] gap-x-2 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-brand-ice/50 sm:text-xs">
                          <span>#</span>
                          <span>Giocatore</span>
                          <span className="text-right">Rating</span>
                          <span className="text-right">Part.</span>
                          <span className="text-right">Vinte</span>
                          <span className="text-right">WR</span>
                        </div>
                        {rest.map((row, idx) => (
                          <div
                            key={row.memberId ?? idx}
                            className={cn(
                              'grid grid-cols-[2.5rem_minmax(0,1fr)_3.5rem_3.5rem_3.5rem_3rem] gap-x-2 px-3 py-2 text-sm text-brand-ice',
                              idx % 2 === 1 && 'bg-surface-muted/20'
                            )}
                          >
                            <span className="tabular-nums text-brand-ice/70">{4 + idx}</span>
                            <span className="min-w-0 truncate">{getRankingMemberDisplayName(row.memberId)}</span>
                            <span className="text-right tabular-nums">{row.finalRating ?? '—'}</span>
                            <span className="text-right tabular-nums">{row.matchesPlayed ?? '—'}</span>
                            <span className="text-right tabular-nums">{row.matchesWon ?? '—'}</span>
                            <span className="text-right tabular-nums">
                              {typeof row.winRate === 'number' ? `${row.winRate}%` : '—'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog Invita membro */}
      {inviteDialogOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="invite-title">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Chiudi"
            onClick={() => setInviteDialogOpen(false)}
          />
          <Card className="relative z-10 flex max-h-[85vh] w-full max-w-md flex-col border-surface-muted bg-surface-card shadow-xl">
            <CardContent className="flex min-h-0 flex-1 flex-col space-y-4 p-6">
              <h2 id="invite-title" className="font-display text-lg font-semibold text-brand-ice">
                Invita membro
              </h2>
              <div className="relative">
                <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-ice/40" />
                <Input
                  className="pl-9"
                  placeholder="Cerca per email o nickname..."
                  value={inviteSearch}
                  onChange={(e) => setInviteSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleInviteSearch())}
                />
              </div>
              <Button type="button" size="sm" className="w-fit" onClick={handleInviteSearch} disabled={inviteSearchLoading}>
                {inviteSearchLoading ? 'Ricerca...' : 'Cerca'}
              </Button>
              <ul className="min-h-0 flex-1 list-none space-y-0 divide-y divide-surface-muted overflow-y-auto rounded-md border border-surface-muted">
                {inviteSearchResults.map((u) => {
                  const alreadyIn = memberIds.has(u.id);
                  return (
                    <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-brand-ice">
                          {u[USERS_FIELDS.DISPLAY_NAME] || u[USERS_FIELDS.EMAIL]}
                        </p>
                        <p className="truncate text-xs text-brand-ice/50">{u[USERS_FIELDS.EMAIL]}</p>
                      </div>
                      {alreadyIn ? (
                        <Badge variant="outline" className="shrink-0">
                          Già nel gruppo
                        </Badge>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleInviteUser(u.id)}
                          disabled={inviteSendingId === u.id}
                        >
                          {inviteSendingId === u.id ? 'Invio...' : 'Invita'}
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="flex justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)}>
                  Chiudi
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog Aggiungi ospite */}
      {guestDialogOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="guest-title">
          <button
            type="button"
            className="absolute inset-0 bg-black/60"
            aria-label="Chiudi"
            onClick={() => !addGuestLoading && setGuestDialogOpen(false)}
          />
          <Card className="relative z-10 w-full max-w-md border-surface-muted bg-surface-card shadow-xl">
            <CardContent className="space-y-4 p-6">
              <h2 id="guest-title" className="font-display text-lg font-semibold text-brand-ice">
                Aggiungi ospite
              </h2>
              {addGuestError && (
                <div className="flex items-start justify-between gap-2 rounded-btn border border-loss/60 bg-loss/15 px-3 py-2 text-sm text-red-100">
                  <span>{addGuestError}</span>
                  <button type="button" className="shrink-0 text-red-200/90 hover:text-red-50" onClick={() => setAddGuestError('')}>
                    Chiudi
                  </button>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="guest-nickname">Nickname ospite</Label>
                <Input
                  id="guest-nickname"
                  value={guestNickname}
                  onChange={(e) => setGuestNickname(e.target.value)}
                  placeholder="Es. Mario Ospite"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest-rating">Rating iniziale</Label>
                <Input
                  id="guest-rating"
                  type="number"
                  min={0}
                  max={3000}
                  step={1}
                  value={guestRating}
                  onChange={(e) => setGuestRating(e.target.value)}
                />
                <p className="text-xs text-brand-ice/50">Opzionale, tra 0 e 3000 (default 1000)</p>
              </div>
              <p className="text-sm text-brand-ice/60">
                L&apos;ospite sarà visibile solo in questo gruppo e non potrà accedere all&apos;app.
              </p>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setGuestDialogOpen(false)} disabled={addGuestLoading}>
                  Annulla
                </Button>
                <Button type="button" onClick={handleAddGuest} disabled={addGuestLoading}>
                  {addGuestLoading ? 'Creazione...' : 'Aggiungi'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
